import noop from 'lodash/noop'
import {
  createContext,
  ReactElement,
  ReactNode,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useSelectedItems } from '../hooks/useSelectedItems'
import { ComboBoxDropdownItem } from '../types/comboBoxDropdownItem'
import { ComboBoxDropdownSourceItem } from '../types/comboBoxDropdownSourceItem'
import { ComboBoxItem } from '../types/comboBoxItem'
import { ComboBoxSelectedItem } from '../types/comboBoxSelectedItem'
import { ComboBoxValue } from '../types/comboBoxValue'
import { getItemFromValue } from '../utils/getItemFromValue'
import { getItems } from '../utils/getItems'
import { getSelectedItemsFromValues } from '../utils/getSelectedItemsFromValues'
import { getSelectedValuesFromSelectedItems } from '../utils/getSelectedValuesFromSelectedItems'

type FormatDisplayedValue = (value: string) => string

export interface ContextState {
  addItemToSelected: (value: ComboBoxItem) => void
  addValueToSelected: () => void
  formatDisplayedValue?: FormatDisplayedValue
  inputValue: string
  isDropdownInsideClickedRef: RefObject<boolean>
  isDropdownOpen: boolean
  isItemListEmpty: boolean
  isItemSelected: (item: ComboBoxItem | ComboBoxSelectedItem) => boolean
  isMaxItemsLimitReached: boolean
  isMultiselect: boolean
  isValueInSelectedItems: (value: string) => boolean
  items: ComboBoxDropdownItem[]
  removeItemFromSelected: (valueToRemove: ComboBoxValue) => void
  selectedItems: ComboBoxSelectedItem[]
  setInputValue: (value: string) => void
  setIsDropdownInsideClickedRef: (value: boolean) => void
  setIsDropdownOpen: (isDropdownOpen: boolean) => void
}

const defaultValue: ContextState = {
  addItemToSelected: noop,
  addValueToSelected: noop,
  formatDisplayedValue: undefined,
  inputValue: '',
  isDropdownInsideClickedRef: { current: false },
  isDropdownOpen: false,
  isItemListEmpty: false,
  isItemSelected: () => false,
  isMaxItemsLimitReached: false,
  isMultiselect: false,
  isValueInSelectedItems: () => false,
  items: [],
  removeItemFromSelected: noop,
  selectedItems: [],
  setInputValue: noop,
  setIsDropdownInsideClickedRef: noop,
  setIsDropdownOpen: noop,
}

const ComboBoxContext = createContext<ContextState>(defaultValue)

interface ContextProviderSharedProps<T> {
  children?: ReactNode
  formatDisplayedValue?: FormatDisplayedValue
  initialInputValue?: string
  items: ComboBoxDropdownSourceItem<T>[]
  maxItems?: number
  selectedValues?: ComboBoxValue[]
  validateItem?: (value: string) => boolean
}

type MultiSelectContextProviderProps<T> = ContextProviderSharedProps<T> & {
  onChange?: (values: ComboBoxValue[]) => void
}

type SingleSelectContextProviderProps<T> = ContextProviderSharedProps<T> & {
  onChange?: (value: ComboBoxValue) => void
}

export type ComboBoxContextProviderProps<T> =
  | ({ isMultiselect: true } & MultiSelectContextProviderProps<T>)
  | ({ isMultiselect: false } & SingleSelectContextProviderProps<T>)

export const ComboBoxContextProvider = <T,>({
  children,
  formatDisplayedValue,
  initialInputValue = '',
  isMultiselect,
  items: itemList,
  maxItems,
  onChange,
  selectedValues,
  validateItem,
}: ComboBoxContextProviderProps<T>): ReactElement => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [inputValue, setInputValue] = useState<string>(initialInputValue)
  const {
    addItem,
    addItems,
    isItemSelected,
    isMaxItemsLimitReached,
    isValueInSelectedItems,
    removeItem,
    selectedItems,
    setSingleItem,
  } = useSelectedItems(isMultiselect ? maxItems : undefined)

  const areInitialValuesSet = useRef(false)
  const isDropdownInsideClickedRef = useRef(false) // helper for not clearing data on blur, after selecting some item (check handleInputBlur)

  const isOpenWithSingleValueSelected = !isMultiselect && isDropdownOpen && inputValue === selectedItems?.[0]?.children

  const items: ComboBoxDropdownItem[] = useMemo(() => {
    return getItems({ items: itemList, isOpenWithSingleValueSelected, isValueInSelectedItems, searchQuery: inputValue })
  }, [inputValue, isOpenWithSingleValueSelected, isValueInSelectedItems, itemList])

  const isItemListEmpty = items?.length === 0

  const isItemValid = useCallback(
    (item: ComboBoxItem) => (validateItem ? validateItem(item.value.value) : true),
    [validateItem],
  )

  const setIsDropdownInsideClickedRef = useCallback((value: boolean) => {
    isDropdownInsideClickedRef.current = value
  }, [])

  const getValidatedItem = useCallback(
    (item: ComboBoxItem): ComboBoxSelectedItem => {
      const isValid = isItemValid(item)

      return { ...item, isValid }
    },
    [isItemValid],
  )

  const onSelectedItemsChange = useCallback(
    (items: ComboBoxSelectedItem[]) => {
      if (!onChange) {
        return
      }

      const selectedValues = getSelectedValuesFromSelectedItems(items)

      if (isMultiselect) {
        onChange?.(selectedValues)
      } else {
        onChange?.(selectedValues[0])
      }
    },
    [isMultiselect, onChange],
  )

  const addItemToSelected = useCallback(
    (item: ComboBoxItem) => {
      const validatedItem = getValidatedItem(item)

      if (isMultiselect) {
        addItem(validatedItem, onSelectedItemsChange)
        setInputValue('')
      } else {
        setSingleItem(validatedItem, onSelectedItemsChange)
        setInputValue(String(validatedItem.children || validatedItem.value.value))
      }
    },
    [addItem, getValidatedItem, isMultiselect, onSelectedItemsChange, setSingleItem],
  )

  const removeItemFromSelected = useCallback(
    (valueToRemove: ComboBoxValue) => {
      removeItem(valueToRemove, onSelectedItemsChange)
    },
    [onSelectedItemsChange, removeItem],
  )

  const addValueToSelected = useCallback(() => {
    if (isMultiselect) {
      const inputValues = inputValue.split(',')
      const newItems: ComboBoxSelectedItem[] = inputValues.map((value) => getValidatedItem(getItemFromValue(value)))

      addItems(newItems, onSelectedItemsChange)

      if (isMultiselect) {
        setInputValue('')
      }
    } else {
      setSingleItem(getValidatedItem(getItemFromValue(inputValue)), onSelectedItemsChange)
    }

    setIsDropdownOpen(false)
  }, [addItems, getValidatedItem, inputValue, isMultiselect, onSelectedItemsChange, setSingleItem])

  useEffect(() => {
    if (!areInitialValuesSet.current) {
      if (isMultiselect && !!selectedValues?.length) {
        const initialSelectedValues = getSelectedItemsFromValues({ isItemValid, items, selectedValues })
        addItems(initialSelectedValues)
        areInitialValuesSet.current = true
      } else if (!isMultiselect && initialInputValue && inputValue) {
        setSingleItem(getValidatedItem(getItemFromValue(inputValue)))
        areInitialValuesSet.current = true
      }
    }
  }, [
    addItems,
    addValueToSelected,
    getValidatedItem,
    initialInputValue,
    inputValue,
    isItemValid,
    isMultiselect,
    items,
    selectedValues,
    setSingleItem,
  ])

  return (
    <ComboBoxContext.Provider
      value={{
        addItemToSelected,
        addValueToSelected,
        formatDisplayedValue,
        inputValue,
        isDropdownInsideClickedRef,
        isDropdownOpen,
        isItemListEmpty,
        isItemSelected,
        isMaxItemsLimitReached,
        isMultiselect,
        isValueInSelectedItems,
        items,
        removeItemFromSelected,
        selectedItems,
        setInputValue,
        setIsDropdownInsideClickedRef,
        setIsDropdownOpen,
      }}
    >
      {children}
    </ComboBoxContext.Provider>
  )
}

export const useComboBox = () => {
  const context = useContext(ComboBoxContext) as ContextState | undefined

  if (!context) {
    throw new Error('useComboBox must be used within a ComboBoxContextProvider')
  }

  return context
}
